import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PermissionGate } from '@/components/PermissionGate';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { 
  ArrowLeft, 
  Download,
  DollarSign,
  CreditCard,
  Banknote,
  CheckCircle,
  Trash2,
  Eye,
  Calendar,
  FileText,
  Wallet,
  Share2,
  Copy,
  Check,
  Link as LinkIcon,
  Edit2,
  Plus,
  X,
  History,
  Mail,
  List
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

// Separate component to avoid hydration warnings from tooling wrapping dynamic content
const InvoiceLineItemsTableBody = ({ items, t }) => (
  <tbody suppressHydrationWarning>
    {items.map((item, index) => (
      <tr key={index} className="border-b border-gray-100" suppressHydrationWarning>
        <td className="py-3">
          <p className="font-medium">{item.name}</p>
          {item.description && (
            <p className="text-sm text-muted-foreground">{item.description}</p>
          )}
        </td>
        <td className="text-right py-3">{item.qty}</td>
        <td className="text-right py-3">${item.rate?.toFixed(2)}</td>
        <td className="text-right py-3 font-medium">${item.total?.toFixed(2)}</td>
      </tr>
    ))}
  </tbody>
);
import PDFPreviewModal from '@/components/PDFPreviewModal';

const PAYMENT_METHODS = [
  { value: 'stripe', label: 'Credit/Debit Card', icon: CreditCard, color: 'text-blue-600' },
  { value: 'square', label: 'Square', icon: CreditCard, color: 'text-gray-600' },
  { value: 'paypal', label: 'PayPal', icon: Wallet, color: 'text-indigo-600' },
  { value: 'venmo', label: 'Venmo', icon: Wallet, color: 'text-blue-500' },
  { value: 'cashapp', label: 'Cash App', icon: DollarSign, color: 'text-green-500' },
  { value: 'cash', label: 'Cash', icon: Banknote, color: 'text-green-600' },
  { value: 'check', label: 'Check', icon: FileText, color: 'text-gray-600' },
  { value: 'other', label: 'Other', icon: Wallet, color: 'text-gray-500' }
];

const InvoiceDetailPage = () => {
  const { invoiceId } = useParams();
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentNotes, setPaymentNotes] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  
  // Edit invoice state
  const [editLineItems, setEditLineItems] = useState([]);
  const [editSummarySections, setEditSummarySections] = useState([]);
  const [editSummaryTotal, setEditSummaryTotal] = useState(0);
  const [editDisplayMode, setEditDisplayMode] = useState('detailed');
  const [editDescription, setEditDescription] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [revisionReason, setRevisionReason] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    loadInvoice();
  }, [invoiceId, organization?.id]);

  const loadInvoice = async () => {
    if (!organization?.id || !invoiceId) return;
    
    try {
      const response = await apiHelpers.getInvoice(organization.id, invoiceId);
      setInvoice(response.data);
      
      // Load client
      const clientRes = await apiHelpers.getClient(organization.id, response.data.client_id);
      setClient(clientRes.data);
      
      // Set default payment amount to balance due (with proper rounding)
      const balance = Math.round((response.data.amount_due - (response.data.amount_paid || 0)) * 100) / 100;
      setPaymentAmount(balance.toFixed(2));
    } catch (error) {
      console.error('Failed to load invoice:', error);
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setIsRecording(true);
    try {
      const response = await apiHelpers.recordPayment(
        organization.id,
        invoiceId,
        amount,
        paymentMethod,
        paymentNotes,
        user.id,
        user.name,
        paymentDate ? new Date(paymentDate).toISOString() : null
      );
      
      setInvoice(response.data);
      setShowPaymentModal(false);
      setPaymentNotes('');
      setPaymentDate(format(new Date(), 'yyyy-MM-dd'));
      
      const methodLabel = PAYMENT_METHODS.find(m => m.value === paymentMethod)?.label || paymentMethod;
      toast.success('Payment recorded!', {
        description: `$${amount.toFixed(2)} via ${methodLabel}`
      });
      
      // Update default payment amount for next time (with proper rounding)
      const newBalance = Math.round((response.data.amount_due - (response.data.amount_paid || 0)) * 100) / 100;
      setPaymentAmount(newBalance.toFixed(2));
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setIsRecording(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await apiHelpers.getInvoicePdf(organization.id, invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm({
      title: 'Delete Invoice?',
      description: 'This action cannot be undone. The invoice will be permanently removed.',
      variant: 'danger',
      confirmText: 'Delete',
      destructive: true
    });
    
    if (!confirmed) return;
    
    try {
      await apiHelpers.deleteInvoice(organization.id, invoiceId, user.id);
      toast.success('Invoice deleted');
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  // Send invoice email to client
  const handleSendInvoiceEmail = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/invoices/${invoiceId}/send?include_pdf=true`,
        { method: 'POST' }
      );
      
      const result = await response.json();
      
      if (response.ok) {
        if (result.email_sent) {
          toast.success('Invoice sent to client!');
        } else {
          toast.info('Invoice ready to share. Client has no email on file - use the share link.');
        }
      } else {
        toast.error(result.detail || 'Failed to send invoice');
      }
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  // Open edit modal with current invoice data
  const openEditModal = () => {
    if (invoice.status === 'paid') {
      toast.error('Cannot edit a paid invoice', {
        description: 'Paid invoices are locked. Void and recreate if changes are needed.'
      });
      return;
    }
    setEditLineItems(invoice.line_items?.map(item => ({
      name: item.name || '',
      description: item.description || '',
      qty: item.qty || 1,
      rate: item.rate || 0
    })) || [{ name: '', description: '', qty: 1, rate: 0 }]);
    setEditSummarySections(invoice.summary_sections?.map(section => ({
      title: section.title || '',
      description: section.description || ''
    })) || [{ title: '', description: '' }]);
    // For summary mode, use amount_due as the grand total
    setEditSummaryTotal(invoice.display_mode === 'summary' ? (invoice.amount_due || 0) : 0);
    setEditDisplayMode(invoice.display_mode || 'detailed');
    setEditDescription(invoice.description || '');
    setEditDueDate(invoice.due_date ? format(new Date(invoice.due_date), 'yyyy-MM-dd') : '');
    setRevisionReason('');
    setShowEditModal(true);
  };

  // Summary section helpers
  const addSummarySection = () => {
    setEditSummarySections([...editSummarySections, { title: '', description: '' }]);
  };

  const removeSummarySection = (index) => {
    if (editSummarySections.length <= 1) return;
    setEditSummarySections(editSummarySections.filter((_, i) => i !== index));
  };

  const updateSummarySection = (index, field, value) => {
    const updated = [...editSummarySections];
    updated[index] = { ...updated[index], [field]: value };
    setEditSummarySections(updated);
  };

  // Add new line item
  const addLineItem = () => {
    setEditLineItems([...editLineItems, { name: '', description: '', qty: 1, rate: 0 }]);
  };

  // Remove line item
  const removeLineItem = (index) => {
    setEditLineItems(editLineItems.filter((_, i) => i !== index));
  };

  // Update line item field
  const updateLineItem = (index, field, value) => {
    const updated = [...editLineItems];
    updated[index] = { ...updated[index], [field]: value };
    setEditLineItems(updated);
  };

  // Calculate edit total based on display mode
  const editTotal = editDisplayMode === 'summary'
    ? parseFloat(editSummaryTotal) || 0
    : editLineItems.reduce((sum, item) => sum + (item.qty * item.rate), 0);

  // Handle update invoice
  const handleUpdateInvoice = async () => {
    if (editDisplayMode === 'summary') {
      // Validate summary sections
      const validSections = editSummarySections.filter(s => s.title.trim());
      if (validSections.length === 0) {
        toast.error('Invoice must have at least one section with a title');
        return;
      }
      // Validate grand total
      if (!editSummaryTotal || editSummaryTotal <= 0) {
        toast.error('Please enter a grand total amount');
        return;
      }
    } else {
      // Validate line items
      if (editLineItems.length === 0) {
        toast.error('Invoice must have at least one line item');
        return;
      }
      for (const item of editLineItems) {
        if (!item.name.trim()) {
          toast.error('All line items must have a name');
          return;
        }
      }
    }
    
    setIsUpdating(true);
    try {
      const updateData = {
        display_mode: editDisplayMode,
        description: editDescription || null,
        due_date: editDueDate || null,
        revision_reason: revisionReason || 'Invoice updated'
      };
      
      if (editDisplayMode === 'summary') {
        updateData.summary_sections = editSummarySections.filter(s => s.title.trim()).map(section => ({
          title: section.title,
          description: section.description
        }));
        updateData.amount_due = parseFloat(editSummaryTotal) || 0;
      } else {
        updateData.line_items = editLineItems.map(item => ({
          name: item.name,
          description: item.description,
          qty: parseFloat(item.qty) || 1,
          rate: parseFloat(item.rate) || 0
        }));
      }
      
      const response = await apiHelpers.updateInvoice(
        organization.id, 
        invoiceId, 
        updateData,
        user.id,
        user.name
      );
      
      setInvoice(response.data);
      setShowEditModal(false);
      
      // Update balance for payment form
      const newBalance = response.data.amount_due - (response.data.amount_paid || 0);
      setPaymentAmount(newBalance.toFixed(2));
      
      toast.success(t('saved_successfully'), {
        description: editTotal !== invoice.amount_due ? 
          `Total updated: $${invoice.amount_due.toFixed(2)} → $${editTotal.toFixed(2)}` : 
          'Invoice details updated'
      });
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error(t('failed_to_save'));
    } finally {
      setIsUpdating(false);
    }
  };

  const statusColors = {
    unpaid: 'bg-red-100 text-red-700',
    partial: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700'
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice) return null;

  // Fix floating point precision - round to 2 decimal places
  const balanceDue = Math.round((invoice.amount_due - (invoice.amount_paid || 0)) * 100) / 100;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/invoices')}
            className="p-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl md:text-3xl font-bold">
                Invoice #{invoiceId.slice(0, 8).toUpperCase()}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${statusColors[invoice.status]}`}>
                {invoice.status.toUpperCase()}
              </span>
            </div>
            <p className="text-muted-foreground">
              Created {formatDistanceToNow(new Date(invoice.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {invoice.status !== 'paid' && (
            <Button
              onClick={() => setShowPaymentModal(true)}
              className="btn-secondary"
              data-testid="record-payment-btn"
            >
              <DollarSign className="w-5 h-5 mr-2" />
              {t('record_payment')}
            </Button>
          )}
          
          <Button
            onClick={() => setShowShareModal(true)}
            variant="outline"
            className="h-12"
            data-testid="share-invoice-btn"
          >
            <Share2 className="w-5 h-5 mr-2" />
            {t('share_link')}
          </Button>
          
          <PermissionGate permission="send_invoices">
            <Button
              onClick={handleSendInvoiceEmail}
              variant="outline"
              className="h-12"
              data-testid="send-invoice-btn"
            >
              <Mail className="w-5 h-5 mr-2" />
              Send Invoice
            </Button>
          </PermissionGate>
          
          <Button
            onClick={openEditModal}
            variant="outline"
            className="h-12"
            data-testid="edit-invoice-btn"
          >
            <Edit2 className="w-5 h-5 mr-2" />
            {t('edit')}
          </Button>
          
          <Button
            onClick={() => setShowPdfPreview(true)}
            variant="outline"
            className="h-12"
            data-testid="preview-pdf-btn"
          >
            <Eye className="w-5 h-5 mr-2" />
            {t('preview_pdf')}
          </Button>
          
          <Button
            onClick={handleDownloadPdf}
            variant="outline"
            className="h-12"
            data-testid="download-pdf-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            {t('download_pdf')}
          </Button>
          
          <Button
            onClick={handleDelete}
            variant="outline"
            className="h-12 text-destructive hover:bg-destructive hover:text-white"
            data-testid="delete-invoice-btn"
          >
            <Trash2 className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Balance Due Banner */}
      {invoice.status !== 'paid' && (
        <div className="card-default p-6 bg-red-50 border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-red-600" />
              </div>
              <div>
                <p className="text-red-700 font-medium">{t('balance_due')}</p>
                <p className="font-heading text-3xl font-bold text-red-700">
                  ${balanceDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            {invoice.due_date && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">{t('due_date')}</p>
                <p className="font-medium text-red-700">
                  {new Date(invoice.due_date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {invoice.status === 'paid' && (
        <div className="card-default p-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <p className="text-green-700 font-medium">{t('paid_in_full')}</p>
              <p className="font-heading text-3xl font-bold text-green-700">
                ${invoice.amount_due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Info */}
          <div className="card-default">
            <h2 className="font-heading text-xl font-semibold mb-4">{t('bill_to')}</h2>
            <div>
              <p className="font-semibold text-lg">{client?.name}</p>
              {client?.email && <p className="text-muted-foreground">{client.email}</p>}
              {client?.phone && <p className="text-muted-foreground">{client.phone}</p>}
              {client?.address && <p className="text-muted-foreground">{client.address}</p>}
            </div>
          </div>
          
          {/* Line Items / Summary Sections */}
          <div className="card-default">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold">
                {invoice.display_mode === 'summary' ? 'Invoice Details' : t('line_items')}
              </h2>
              {invoice.display_mode === 'summary' && (
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">Summary</span>
              )}
            </div>
            
            {/* Summary Mode Display */}
            {invoice.display_mode === 'summary' && (
              <div className="space-y-4">
                {invoice.description && (
                  <p className="text-muted-foreground mb-4">{invoice.description}</p>
                )}
                {invoice.summary_sections?.map((section, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-primary/10 p-3 flex justify-between items-center">
                      <span className="font-semibold">{section.title}</span>
                      <span className="font-bold text-primary">${parseFloat(section.price || 0).toFixed(2)}</span>
                    </div>
                    {section.description && (
                      <div className="p-3 text-sm text-muted-foreground whitespace-pre-line bg-gray-50">
                        {section.description}
                      </div>
                    )}
                  </div>
                ))}
                {(!invoice.summary_sections || invoice.summary_sections.length === 0) && (
                  <p className="text-muted-foreground text-center py-4">No sections</p>
                )}
              </div>
            )}
            
            {/* Itemized Mode Display */}
            {invoice.display_mode !== 'summary' && (
              <>
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 font-medium">{t('item')}</th>
                    <th className="text-right py-3 font-medium w-20">{t('qty')}</th>
                    <th className="text-right py-3 font-medium w-24">{t('rate')}</th>
                    <th className="text-right py-3 font-medium w-28">{t('total')}</th>
                  </tr>
                </thead>
                <InvoiceLineItemsTableBody items={invoice.line_items || []} t={t} />
              </table>
            </div>
            
            {/* Mobile Card Layout */}
            <div className="sm:hidden space-y-3">
              {invoice.line_items?.map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-semibold">{item.name}</p>
                      {item.description && (
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                    <p className="font-bold text-lg">${item.total?.toFixed(2)}</p>
                  </div>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>{item.qty} {t('qty').toLowerCase()}</span>
                    <span>×</span>
                    <span>${item.rate?.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
              </>
            )}
            
            {/* Totals */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between py-2 text-xl font-bold">
                <span>{t('total')}</span>
                <span>${invoice.amount_due?.toFixed(2)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <>
                  <div className="flex justify-between py-2 text-green-600">
                    <span>{t('paid')}</span>
                    <span>-${invoice.amount_paid?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 text-xl font-bold border-t border-gray-200">
                    <span>{t('balance_due')}</span>
                    <span className={balanceDue > 0 ? 'text-red-600' : 'text-green-600'}>
                      ${balanceDue.toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment History */}
          <div className="card-default">
            <h2 className="font-heading text-xl font-semibold mb-4">{t('payment_history')}</h2>
            {invoice.payments && invoice.payments.length > 0 ? (
              <div className="space-y-3">
                {invoice.payments.map((payment, index) => (
                  <div key={index} className="p-3 bg-green-50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {payment.method === 'cash' && <Banknote className="w-4 h-4 text-green-600" />}
                        {payment.method === 'check' && <Banknote className="w-4 h-4 text-green-600" />}
                        {payment.method === 'stripe' && <CreditCard className="w-4 h-4 text-green-600" />}
                        <span className="font-medium capitalize">{payment.method}</span>
                      </div>
                      <span className="font-bold text-green-700">${payment.amount?.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(payment.date).toLocaleDateString()}
                    </p>
                    {payment.notes && (
                      <p className="text-sm text-muted-foreground">{payment.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">{t('no_payments')}</p>
            )}
          </div>
          
          {/* Revision History */}
          {invoice.revisions && invoice.revisions.length > 0 && (
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-muted-foreground" />
                {t('revision_history')}
              </h2>
              <div className="space-y-3">
                {invoice.revisions.map((revision, index) => (
                  <div key={revision.id || index} className="p-3 bg-amber-50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-amber-800 font-medium">
                        ${revision.previous_total?.toFixed(2)} → ${revision.new_total?.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">{revision.reason}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {revision.revised_by_name && `by ${revision.revised_by_name} • `}
                      {new Date(revision.revised_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Quick Actions - Record Payment */}
          {invoice.status !== 'paid' && (
            <PermissionGate permission="record_payments">
              <div className="card-default">
                <h2 className="font-heading text-xl font-semibold mb-4">Record Payment</h2>
                <p className="text-muted-foreground text-sm mb-4">Select how the customer paid to record it</p>
                
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {PAYMENT_METHODS.map((method) => (
                    <Button
                      key={method.value}
                      onClick={() => {
                        setPaymentMethod(method.value);
                        setShowPaymentModal(true);
                      }}
                      variant="outline"
                      className="h-12 sm:h-14 justify-start text-sm sm:text-base hover:bg-gray-50 px-2 sm:px-4"
                      data-testid={`record-${method.value}-btn`}
                    >
                      <method.icon className={`w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0 ${method.color}`} />
                      <span className="truncate">{method.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </PermissionGate>
          )}
        </div>
      </div>
      
      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl sm:text-2xl">Mark as Paid</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm sm:text-base">Amount</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-base sm:text-lg">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="input-default pl-7 sm:pl-8 h-10 sm:h-11"
                    data-testid="payment-amount-input"
                  />
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Balance: ${balanceDue.toFixed(2)}
                </p>
              </div>
              
              <div>
                <Label className="text-sm sm:text-base">Payment Date</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="input-default pl-10 h-10 sm:h-11"
                    data-testid="payment-date-input"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-sm sm:text-base">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="input-default mt-1 h-10 sm:h-11" data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      <span className="flex items-center gap-2">
                        <method.icon className={`w-4 h-4 ${method.color}`} />
                        {method.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm sm:text-base">Notes (Optional)</Label>
              <Textarea
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Check #, Transaction ID..."
                className="mt-1 min-h-[60px] sm:min-h-[80px] text-sm"
                data-testid="payment-notes-input"
              />
            </div>
            
            {(paymentMethod === 'stripe' || paymentMethod === 'square') && (
              <div className="p-3 sm:p-4 bg-blue-50 rounded-xl">
                <p className="text-xs sm:text-sm text-blue-800">
                  <strong>Demo Mode:</strong> Online payments simulated.
                </p>
              </div>
            )}
            
            <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
              <Button
                variant="outline"
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 h-10 sm:h-12 text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRecordPayment}
                disabled={isRecording || !paymentAmount}
                className="flex-1 h-10 sm:h-12 btn-primary text-sm"
                data-testid="confirm-payment-btn"
              >
                {isRecording ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="hidden sm:inline">Processing...</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    Record
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        pdfUrl={organization?.id && invoiceId ? `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/invoices/${invoiceId}/pdf` : null}
        title="Invoice Preview"
        documentNumber={invoiceId?.slice(0, 8).toUpperCase()}
      />
      
      {/* Share Invoice Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-2xl flex items-center gap-2">
              <LinkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              Share Payment Link
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <p className="text-sm text-muted-foreground">
              Send this link to your client to view and pay online.
            </p>
            
            <div className="flex gap-2">
              <Input
                readOnly
                value={`${window.location.origin}/invoice/view/${invoice?.public_payment_token || ''}`}
                className="input-default flex-1 text-xs sm:text-sm h-10"
                data-testid="share-link-input"
              />
              <Button
                onClick={async () => {
                  const link = `${window.location.origin}/invoice/view/${invoice?.public_payment_token}`;
                  try {
                    if (navigator.clipboard && window.isSecureContext) {
                      await navigator.clipboard.writeText(link);
                    } else {
                      const textArea = document.createElement('textarea');
                      textArea.value = link;
                      textArea.style.position = 'fixed';
                      textArea.style.left = '-9999px';
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                    }
                    setLinkCopied(true);
                    toast.success('Link copied!');
                    setTimeout(() => setLinkCopied(false), 2000);
                  } catch (error) {
                    toast.info(`Copy this link: ${link}`, { duration: 8000 });
                  }
                }}
                className="btn-primary h-10 px-3"
                data-testid="copy-share-link-btn"
              >
                {linkCopied ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </Button>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-amber-800">
                <strong>Tip:</strong> This link shows your branding and payment options.
              </p>
            </div>
            
            <Button
              variant="outline"
              className="w-full h-10 sm:h-12 text-sm"
              onClick={() => {
                const link = `${window.location.origin}/invoice/view/${invoice?.public_payment_token}`;
                window.open(link, '_blank');
              }}
            >
              <Eye className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Preview as Client
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Invoice Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-2xl flex items-center gap-2">
              <Edit2 className="w-5 h-5 sm:w-6 sm:h-6" />
              {t('edit')} Invoice
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6 mt-3 sm:mt-4">
            {/* Due Date */}
            <div>
              <Label className="text-sm sm:text-base">{t('due_date')}</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                  className="input-default pl-10 h-10 sm:h-11"
                  data-testid="edit-due-date-input"
                />
              </div>
            </div>
            
            {/* Summary vs Detailed Toggle */}
            <div>
              <Label className="text-sm sm:text-base mb-2 block">Invoice Format</Label>
              <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => setEditDisplayMode('summary')}
                  className={`flex-1 py-2.5 px-3 rounded-lg font-medium transition-all text-sm ${
                    editDisplayMode === 'summary' 
                      ? 'bg-white shadow-sm text-primary' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-1.5" />
                  Summary
                </button>
                <button
                  type="button"
                  onClick={() => setEditDisplayMode('detailed')}
                  className={`flex-1 py-2.5 px-3 rounded-lg font-medium transition-all text-sm ${
                    editDisplayMode === 'detailed' 
                      ? 'bg-white shadow-sm text-primary' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <List className="w-4 h-4 inline mr-1.5" />
                  Itemized
                </button>
              </div>
            </div>
            
            {/* Summary Mode */}
            {editDisplayMode === 'summary' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Summary Mode:</strong> Add sections with descriptions. Enter the grand total at the bottom.
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm">Quick Description (optional)</Label>
                  <Input
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="e.g., Driveway concrete work"
                    className="input-default mt-1"
                  />
                </div>
                
                {editSummarySections.map((section, index) => (
                  <div key={index} className="p-3 border border-gray-200 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">Section {index + 1}</span>
                      {editSummarySections.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive h-8"
                          onClick={() => removeSummarySection(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Title (e.g., Driveway Work)"
                      value={section.title}
                      onChange={(e) => updateSummarySection(index, 'title', e.target.value)}
                      className="input-default h-9 text-sm"
                    />
                    <textarea
                      placeholder={"• Scope details\n• Materials included"}
                      value={section.description}
                      onChange={(e) => updateSummarySection(index, 'description', e.target.value)}
                      className="w-full h-20 p-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSummarySection}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Section
                </Button>
                
                {/* Grand Total Input */}
                <div className="p-3 sm:p-4 bg-primary/5 border border-primary/20 rounded-xl">
                  <Label className="text-sm font-semibold">Grand Total *</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editSummaryTotal || ''}
                      onChange={(e) => setEditSummaryTotal(parseFloat(e.target.value) || 0)}
                      className="input-default pl-8 h-12 text-xl font-bold"
                      placeholder="0.00"
                      data-testid="invoice-summary-grand-total"
                    />
                  </div>
                  {editTotal !== invoice.amount_due && invoice.amount_due > 0 && (
                    <p className="text-xs sm:text-sm text-amber-600 mt-2">
                      Changed from ${invoice.amount_due.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Itemized Mode - Line Items */}
            {editDisplayMode === 'detailed' && (
            <div>
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <Label className="text-sm sm:text-base">{t('line_items')}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLineItem}
                  className="h-8 text-xs sm:text-sm"
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('add')}</span>
                </Button>
              </div>
              
              <div className="space-y-3">
                {editLineItems.map((item, index) => (
                  <div key={index} className="p-3 sm:p-4 border border-gray-200 rounded-xl space-y-2 sm:space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2 sm:space-y-3">
                        <Input
                          placeholder={t('item')}
                          value={item.name}
                          onChange={(e) => updateLineItem(index, 'name', e.target.value)}
                          className="input-default h-9 sm:h-10 text-sm"
                          data-testid={`line-item-${index}-name`}
                        />
                        <Input
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          className="input-default h-9 sm:h-10 text-sm"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('qty')}</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.qty}
                              onChange={(e) => updateLineItem(index, 'qty', parseFloat(e.target.value) || 1)}
                              className="input-default h-9 sm:h-10 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('rate')}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.rate}
                              onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                              className="input-default h-9 sm:h-10 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">{t('total')}</Label>
                            <div className="h-9 sm:h-10 px-2 sm:px-4 flex items-center bg-gray-100 rounded-lg font-semibold text-sm">
                              ${(item.qty * item.rate).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeLineItem(index)}
                        disabled={editLineItems.length <= 1}
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* New Total */}
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm sm:text-base">New Total:</span>
                  <span className="font-heading text-xl sm:text-2xl font-bold">
                    ${editTotal.toFixed(2)}
                  </span>
                </div>
                {editTotal !== invoice.amount_due && (
                  <p className="text-xs sm:text-sm text-amber-600 mt-1">
                    Changed from ${invoice.amount_due.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
            )}
            
            {/* Revision Reason */}
            {editTotal !== invoice.amount_due && (
              <div>
                <Label className="text-sm sm:text-base">Reason for Change</Label>
                <Textarea
                  value={revisionReason}
                  onChange={(e) => setRevisionReason(e.target.value)}
                  placeholder="e.g., Added work..."
                  className="mt-1 min-h-[60px] sm:min-h-[80px] text-sm"
                  data-testid="revision-reason-input"
                />
              </div>
            )}
            
            {/* Warning about payments */}
            {invoice.amount_paid > 0 && (
              <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-xs sm:text-sm text-blue-800">
                  <strong>Note:</strong> ${invoice.amount_paid.toFixed(2)} already paid. Balance will be recalculated.
                </p>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1 h-10 sm:h-12 text-sm"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleUpdateInvoice}
                disabled={isUpdating || (editDisplayMode === 'summary' ? editSummarySections.filter(s => s.title.trim()).length === 0 : editLineItems.length === 0)}
                className="flex-1 h-10 sm:h-12 btn-primary text-sm"
                data-testid="save-invoice-btn"
              >
                {isUpdating ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('saving')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {t('save')}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
    </div>
  );
};

export default InvoiceDetailPage;
